import React, { useState, useEffect } from "react";
import {
  Box,
  Divider as MuiDivider,
  Typography,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Avatar,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFilterNavigation } from "../../../hooks/useFilterNavigation";
import {
  setLibriMatricolaFilters,
  clearLibriMatricolaFilters,
} from "../../../redux/slices/libriMatricolaFilters";
import { API_BASE_URL } from "../../../config";
import { Book } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { deepOrange } from "@mui/material/colors";

const Divider = styled(MuiDivider)(spacing);

function LibriMatricola() {
  const dispatch = useDispatch();
  const filterData = useSelector((state) => state.libriMatricolaFilters);

  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState(filterData || "");

  const [totalRows, setTotalRows] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [loadingPage, setLoadingPage] = useState(true);

  async function apiTestvinz() {
    const token = sessionStorage.getItem("token");

    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
      };

      const response = await fetch(
        `${API_BASE_URL}/api/lmGetList`,
        requestOptions,
      );

      const responseData = await response.json();
      console.log("Risposta API:", responseData); // Per verificare la risposta

      if (responseData.status === -1) {
        sessionStorage.clear();
        navigate("/auth/sign-in");
      }
      setRows(responseData.data.dataset || []);
      setTotalRows(responseData.data.dataset.length);
    } catch (error) {
      console.error("Errore nella chiamata API:", error);
      return error;
    }
  }

  useEffect(() => {
    apiTestvinz()
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoadingPage(false));
  }, []);

  useFilterNavigation(
    () => {
      if (filterData) {
        setSearch(filterData);
      }
    },
    () => {
      dispatch(clearLibriMatricolaFilters());
      setSearch("");
    },
  );

  useEffect(() => {
    if (filterData !== null && filterData !== undefined) {
      setSearch(filterData);
    }
  }, [filterData]);

  const columns = [
    {
      field: "Nome",
      headerName: "Nome",
      flex: 1.2,
      minWidth: 250,

      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1} py={1}>
          <Book color="primary" />

          <Box>
            <Link
              underline="hover"
              sx={{ cursor: "pointer", fontWeight: 600 }}
              onClick={() =>
                navigate(`/pages/libriMatricolaDetails/${params.row.idUid}`, {
                  state: { fromDetails: true },
                })
              }
            >
              {params.row.Nome}
            </Link>

            <Typography variant="caption" display="block">
              {params.row.Numero_Polizza}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "Tipo",
      headerName: "Tipo",
      flex: 0.8,
    },
    {
      field: "fornitore",
      headerName: "Fornitore/Contraente",
      flex: 1.2,
      valueGetter: (params) =>
        `${params.row.fornitore}\n${params.row.Contraente_Nota}`,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.fornitore}</Typography>

          <Typography variant="caption">
            {params.row.Contraente_Nota}
          </Typography>
        </Box>
      ),
    },
    {
      field: "Scadenza",
      headerName: "Scadenza",
      width: 120,
    },
    {
      field: "Compagnia",
      headerName: "Compagnia",
      flex: 1,
    },
    {
      field: "utenteNominativo",
      headerName: "Creato da",
      flex: 1,

      renderCell: (params) => (
        <Box display="flex" gap={1} alignItems="center">
          <Avatar sx={{ bgcolor: deepOrange[500] }} />

          <Box>
            <Typography fontWeight={600}>
              {params.row.utenteNominativo}
            </Typography>

            <Typography variant="caption">
              {params.row.DataCreazione}
            </Typography>
          </Box>
        </Box>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Helmet title="Libri Matricola" />
      {loadingPage ? (
        <Box
          sx={{
            width: "calc(100vw - 40px)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <React.Fragment>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h3" display="inline">
              Libri Matricola
            </Typography>
          </Box>
          <Divider sx={{ my: 4 }} />
          <Paper sx={{ p: 2, mt: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography fontWeight={600}>Risultati: {totalRows}</Typography>

              <TextField
                size="small"
                placeholder="Cerca..."
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearch(value);
                  dispatch(setLibriMatricolaFilters(value));
                }}
                sx={{ width: 250 }}
              />
            </Box>
            <DataGrid
              rows={rows.filter((row) => {
                const value = search.toLowerCase();

                return (
                  row.Nome?.toLowerCase().includes(value) ||
                  row.Numero_Polizza?.toLowerCase().includes(value) ||
                  row.Compagnia?.toLowerCase().includes(value) ||
                  row.Tipo?.toLowerCase().includes(value) ||
                  row.fornitore?.toLowerCase().includes(value)
                );
              })}
              columns={columns}
              getRowId={(row) => row.idUid}
              autoHeight
              disableRowSelectionOnClick
              rowsPerPageOptions={[5, 10, 20]}
              pagination
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            />
          </Paper>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default LibriMatricola;
